// https://vee-validate.logaretm.com/v3/overview.html#usage

import Vue from "vue"
import {
  // eslint-disable-next-line camelcase
  email, required, min, max, confirmed, min_value, max_value, double, mimes,
  size, integer, digits,
} from "vee-validate/dist/rules"
import {
  ValidationProvider, ValidationObserver, extend, localize, setInteractionMode,
} from "vee-validate"
import ru from "vee-validate/dist/locale/ru.json"
import en from "vee-validate/dist/locale/en.json"
// import tr from "vee-validate/dist/locale/tr.json"
import { isBirthdateValid, getBirthdateErrorParams } from "@/utils/birthdate"

function cleanFieldPlaceholders (locale: typeof ru) {
  const messages = Object.fromEntries(
    Object.entries(locale.messages)
      .map(m => [m[0], m[1].replace(/ ?\{_?field_?\} ?/, " ")]),
  )
  return { ...locale, messages }
}

const plugin: NuxtPlugin = ({ app: { i18n } }) => {
  setInteractionMode("eager")

  localize({
    ru: cleanFieldPlaceholders(ru),
    en: cleanFieldPlaceholders(en),
    // tr: cleanFieldPlaceholders(tr),
  })
  localize(i18n.locale)

  Vue.component("ValidationObserver", ValidationObserver)
  Vue.component("ValidationProvider", ValidationProvider)

  extend("required", required)
  extend("email", email)
  // extend("numeric", numeric)
  extend("min", min)
  extend("max", max)
  extend("mimes", mimes)
  extend("size", size)
  extend("min_value", min_value)
  extend("max_value", max_value)
  extend("double", double)
  extend("integer", integer)
  extend("confirmed", confirmed)
  extend("digits", digits)

  extend("min_length", {
    validate: (v, { length }) =>
      (v.length >= length) || i18n.t("error.minLength", [length]) as string,
    params: ["length"],
  })
  extend("max_length", {
    validate: (v, { length }) =>
      (v.length <= length) || i18n.t("error.maxLength", [length]) as string,
    params: ["length"],
  })

  extend("ru", v => /^[а-яА-ЯёЁ.,0-9№ -]+$/.test(v) || i18n.t("error.ru") as string)
  extend("en", v => /^[a-zA-Z.,0-9№ -]+$/.test(v) || i18n.t("error.en") as string)

  extend("url", v =>
    /^https?:\/\/.+$/.test(v) || i18n.t("error.url") as string)

  extend("tel", v =>
    /^\+?[1-9][0-9( )-]{7,30}$/.test(v) || i18n.t("error.tel") as string)

  extend("min_date", {
    // @ts-ignore
    validate: (v, { min }) => {
      const ls = new Date(min).toLocaleDateString()
      return new Date(v).getTime() >= min || i18n.t("error.minDate", [ls])
    },
    params: ["min"],
  })

  extend("max_date", {
    // @ts-ignore
    validate: (v, { max }) => {
      const ls = new Date(max).toLocaleDateString()
      return new Date(v).getTime() <= max || i18n.t("error.maxDate", [ls])
    },
    params: ["max"],
  })

  extend("valid_age", {
    params: ["event", "status"],
    validate: (
      birthdate,
      // @ts-ignore
      { event, status },
    ) => {
      const birthdateErrorParams =
        getBirthdateErrorParams(event, i18n.locale, status)
      const birthdateErrorDates = i18n.t(...birthdateErrorParams)

      return isBirthdateValid({
        birthdate,
        event,
        status,
      }) || i18n.t(
        "invalidBirthdateError",
        { dates: birthdateErrorDates },
      ) as string
    },
  })

  extend("latin-num", {
    validate: (v) => {
      return /^[a-zA-Z0-9]*$/.test(v) || i18n.t("onlyLatinOrDigits") as string
    },
  })

  extend("required_checked", {
    validate: value => value || i18n.t("message.validation.required"),
  })
}
export default plugin
